import React, { useState } from "react";
import { SignedIn, SignedOut, useClerk } from "@clerk/clerk-react";
import Sidebar from "./components/Sidebar";
import HomePage from "./components/HomePage";
import FilesPage from "./components/FilesPage";
import DocumentsPage from "./components/DocumentsPage";
import CorporaPage from "./components/CorporaPage";
import ApiExplorerPage from "./components/ApiExplorerPage";
import SkillsPage from "./components/SkillsPage";
import LandingPage from "./components/LandingPage";
import ProfilePage from "./components/ProfilePage";
import { CorporaProvider } from "./lib/useCorpora";

export type Page = "home" | "files" | "documents" | "corpora" | "skills" | "api";

// Clerk's <SignedIn>/<SignedOut> components type their return value as
// `ReactNode` (per React 19 conventions), which the project's current
// @types/react version (18.2) doesn't accept as a valid JSX component
// return type. Re-typing as a plain FC with children resolves this without
// bumping the whole React type stack.
type FCWithChildren = React.FC<{ children?: React.ReactNode }>;
const SignedInGate = SignedIn as unknown as FCWithChildren;
const SignedOutGate = SignedOut as unknown as FCWithChildren;


/**
 * Authenticated dashboard: sidebar + page router.
 * This is only ever rendered once Clerk confirms the user is signed in.
 */
const Dashboard: React.FC = () => {
  const [activePage, setActivePage] = useState<Page>("files");
  const [visitedPages, setVisitedPages] = useState<Set<Page>>(new Set(["files"]));
  const { signOut } = useClerk();

  const handleNavigate = (page: Page) => {
    setVisitedPages((prev) => new Set([...prev, page]));
    setActivePage(page);
  };

  return (
    <CorporaProvider>
    <div className="flex h-screen bg-paper overflow-hidden">
      <Sidebar activePage={activePage} onNavigate={handleNavigate} onSignOut={() => signOut()} />
      <main className="flex-1 overflow-hidden flex flex-col">
        {visitedPages.has("home") && (
          <div className={`flex-1 overflow-hidden flex flex-col${activePage !== "home" ? " hidden" : ""}`}>
            <HomePage />
          </div>
        )}
        {visitedPages.has("files") && (
          <div className={`flex-1 overflow-hidden flex flex-col${activePage !== "files" ? " hidden" : ""}`}>
            <FilesPage />
          </div>
        )}
        {visitedPages.has("documents") && (
          <div className={`flex-1 overflow-hidden flex flex-col${activePage !== "documents" ? " hidden" : ""}`}>
            <DocumentsPage />
          </div>
        )}
        {visitedPages.has("corpora") && (
          <div className={`flex-1 overflow-hidden flex flex-col${activePage !== "corpora" ? " hidden" : ""}`}>
            <CorporaPage />
          </div>
        )}
        {visitedPages.has("skills") && (
          <div className={`flex-1 overflow-hidden flex flex-col${activePage !== "skills" ? " hidden" : ""}`}>
            <SkillsPage />
          </div>
        )}
        {visitedPages.has("api") && (
          <div className={`flex-1 overflow-hidden flex flex-col${activePage !== "api" ? " hidden" : ""}`}>
            <ApiExplorerPage />
          </div>
        )}
      </main>
    </div>
    </CorporaProvider>
  );
};

/**
 * App entry point.
 *
 * - Signed out (default): the kurnal.ai marketing landing page is shown.
 * - Signed in: the full indexing-client dashboard (Sidebar + pages) is shown,
 *   gated behind Clerk authentication.
 */
function App() {
  // Standalone public profile page. Deliberately unauthenticated, reachable
  // only by typing the path — it appears in no menu or navigation.
  const path = window.location.pathname.replace(/\/+$/, "").toLowerCase();
  if (path === "/anthonyculligan") {
    return <ProfilePage />;
  }

  return (
    <>
      <SignedOutGate>
        <LandingPage />
      </SignedOutGate>
      <SignedInGate>
        <Dashboard />
      </SignedInGate>
    </>
  );

}

export default App;
